import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Button, TextField, Select, MenuItem, IconButton, FormControl, InputLabel,
  TablePagination, Grid, Tooltip, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, Checkbox, CircularProgress, Autocomplete, Card, Fade, Snackbar, Alert,
  Container
} from '@mui/material';
import {
  Search as SearchIcon, Edit as EditIcon, Delete as DeleteIcon, AddCircle as AddIcon,
  Close as CloseIcon, Save as SaveIcon, PostAdd as PostAddIcon, EditNote as EditNoteIcon,
  DeleteSweep as DeleteSweepIcon, WarningAmber as WarningIcon
} from '@mui/icons-material';

import Newfooter from '../NewFooter/Newfooter'; 
import { VIPSDataServiceURL, requestOptions } from '../../Constants/GlobalConstant';

// --- CONSTANTS ---
const PRIMARY_COLOR = '#1565c0'; 
const BACKGROUND_BG = '#f0f4f8'; 
const HOVER_ROW_COLOR = '#eef6fc';
const HEADER_BG = '#1e293b'; 
const BUTTON_GRADIENT = 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)';

const REQUEST_TYPES = { CREATE: 2, UPDATE: 3, DELETE: 4 };

const initialFormState = {
  id: 0, 
  divisionId: '',
  storeGroup: '',
  commId: '',
  billToId: '',
  webAccNo: '',
  erpAccNo: ''
};

export default function Ajkcustomer() {
  
  // --- REDUX USER ID ---
  const userPost = useSelector((state) => state.user);
  const loggedInUserId = userPost?.paxWebSessionStrings?.find(
    (item) => item.Key === 'user_id'
  )?.Value || 'System';

  // --- Data State ---
  const [rows, setRows] = useState([]); 
  const [storeOptions, setStoreOptions] = useState([]); 
  const [divisionOptions, setDivisionOptions] = useState([]); 
  const [selectedRow, setSelectedRow] = useState(null); 
  const [loading, setLoading] = useState(false); 
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20); 
  const [filters, setFilters] = useState({ prodLoc: 'ALL', storeGroup: 'ALL', commId: '', billToId: '', erpAccNo: '' });
  const [activeFilters, setActiveFilters] = useState({ prodLoc: 'ALL', storeGroup: 'ALL', commId: '', billToId: '', erpAccNo: '' });
  const [selectedIds, setSelectedIds] = useState([]);
  const [dialogState, setDialogState] = useState({ open: false, type: 'INSERT' });
  const [formData, setFormData] = useState(initialFormState);
  const [deleteConfirmation, setDeleteConfirmation] = useState({ open: false, type: 'single', id: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB'); 
  };

  const premiumInputStyle = {
    '& .MuiOutlinedInput-root': {
        backgroundColor: '#fff', borderRadius: 3, transition: 'all 0.2s ease-in-out',
        '& fieldset': { borderColor: '#e0e0e0', borderWidth: '1.5px' },
        '&:hover fieldset': { borderColor: PRIMARY_COLOR },
        '&.Mui-focused fieldset': { borderColor: PRIMARY_COLOR, borderWidth: '2.5px' },
        '&.Mui-disabled fieldset': { borderColor: '#f0f0f0' },
        '&.Mui-disabled': { backgroundColor: '#f9f9f9', color: '#888' }
    },
    '& .MuiInputLabel-root.Mui-focused': { color: PRIMARY_COLOR, fontWeight: 600 },
  };

  const headerStyle = {
    backgroundColor: HEADER_BG, color: 'white', fontWeight: '600', fontSize: '0.85rem',
    textTransform: 'none', 
    letterSpacing: '0.5px', padding: '12px 16px', whiteSpace: 'nowrap',
    position: 'sticky', 
    top: 0, 
    zIndex: 100 
  };

  const dialogHeaderStyle = {
    backgroundColor: HEADER_BG, color: '#ffffff !important', display: 'flex', 
    justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px', 
    borderBottom: `4px solid ${PRIMARY_COLOR}`
  };

  const menuPropsConfig = {
    PaperProps: { style: { maxHeight: 300, marginTop: 8, borderRadius: 8 } },
    anchorOrigin: { vertical: "bottom", horizontal: "left" },
    transformOrigin: { vertical: "top", horizontal: "left" },
    getContentAnchorEl: null
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (loggedInUserId && loggedInUserId !== 'System') { 
        fetchStoreGroups(); 
        fetchDivisions(); 
    }
  }, [loggedInUserId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const cleanUrl = VIPSDataServiceURL.replace(/\/$/, "");
      const response = await axios.post(`${cleanUrl}/Ajk/GetCustomerMapping`, {}, { headers: { ...requestOptions.headers, 'Content-Type': 'application/json' } });
      const resultData = (typeof response.data === 'string') ? JSON.parse(response.data) : response.data;

      if (resultData) {
        const mappedData = resultData.map((item, index) => {
           const apiId = item.Id || item.id || item.ID || 0;
           return {
            id: apiId ? `${apiId}-${item.divisionId}-${index}` : `row-${index}-${Date.now()}`,
            originalId: apiId, 
            rawItem: item,
            divisionId: item.divisionId ? item.divisionId.toUpperCase() : '',
            commId: item.CommID, 
            billToId: item.BillToId, 
            storeGroup: item.storeGroup,
            webAccNo: item.WebAccount, 
            erpAccNo: item.ERPAccount,
            dateAdded: formatDate(item.added), 
            addedBy: item.addedBy,
            dateUpdated: formatDate(item.updated), 
            updatedBy: item.updatedBy
           };
        });

        mappedData.sort((a, b) => Number(b.originalId || 0) - Number(a.originalId || 0));
        setRows(mappedData); 
      }
    } catch (error) { setSnackbar({ open: true, message: 'Error fetching data', severity: 'error' }); } 
    finally { setLoading(false); }
  };

  const fetchStoreGroups = async () => {
    try {
        const cleanUrl = VIPSDataServiceURL.replace(/\/$/, "");
        const params = new URLSearchParams(); 
        params.append('userId', loggedInUserId); 
        const response = await axios.post(`${cleanUrl}/User/GetUserStores`, null, { params: params, headers: { ...requestOptions.headers, 'Content-Type': 'application/json' } });
        const data = (typeof response.data === 'string') ? JSON.parse(response.data) : response.data;
        if (data) setStoreOptions(data); 
    } catch (error) { }
  };

  const fetchDivisions = async () => {
    try {
        const cleanUrl = VIPSDataServiceURL.replace(/\/$/, "");
        const params = new URLSearchParams(); 
        params.append('userId', loggedInUserId); 
        const response = await axios.post(`${cleanUrl}/User/GetUserDivisions`, null, { params: params, headers: { ...requestOptions.headers, 'Content-Type': 'application/json' } });
        const data = (typeof response.data === 'string') ? JSON.parse(response.data) : response.data;
        if (data) {
            const cleanedOptions = data.map(opt => ({ ...opt, value: opt.value ? opt.value.trim().toUpperCase() : '' }));
            setDivisionOptions(cleanedOptions); 
        }
    } catch (error) { }
  };

  const handleSearch = () => { setActiveFilters({ ...filters }); setPage(0); };

  const handleInputChange = (field, value) => {
    const restrictedFields = ['billToId', 'webAccNo', 'erpAccNo', 'commId'];
    if (restrictedFields.includes(field)) {
      const sanitizedValue = value.replace(/[^a-zA-Z0-9]/g, '');
      setFormData(prev => ({ ...prev, [field]: sanitizedValue }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleCommIdChange = (event, newValue) => {
    const sanitizedValue = newValue ? newValue.replace(/[^a-zA-Z0-9]/g, '') : '';
    let newStoreGroup = '';
    if (sanitizedValue) {
        const match = storeOptions.find(opt => opt.value.toLowerCase() === sanitizedValue.toLowerCase() || opt.label.toLowerCase() === sanitizedValue.toLowerCase());
        newStoreGroup = match ? match.value : sanitizedValue;
    }
    setFormData(prev => ({ ...prev, commId: sanitizedValue, storeGroup: newStoreGroup }));
  };

  const handleStoreGroupChange = (event) => {
    const newValue = event.target.value;
    setFormData(prev => ({ ...prev, storeGroup: newValue, commId: newValue }));
  };

  const handleOpenInsert = () => { 
    setFormData(initialFormState); 
    setSelectedRow(null); 
    setDialogState({ open: true, type: 'INSERT' }); 
  };
   
  const handleOpenEdit = (row) => {
      if (!row.originalId) {
          setSnackbar({ open: true, message: 'Error: Missing Record ID', severity: 'error' });
          return;
      }
      setSelectedRow(row.rawItem);
      setFormData({
          id: row.originalId, 
          divisionId: row.divisionId || '', 
          storeGroup: row.storeGroup || '', 
          commId: row.commId || '',
          billToId: row.billToId || '', 
          webAccNo: row.webAccNo || '', 
          erpAccNo: row.erpAccNo || ''
      });
      setDialogState({ open: true, type: 'EDIT' });
  };

  const handleFormSubmit = (e) => {
      e.preventDefault();
      handleSaveRecord();
  };

  const handleSaveRecord = async () => {
    // Note: Manual validation removed because HTML5 "required" attribute handles it now.
    const { divisionId, storeGroup, commId, billToId, webAccNo, erpAccNo } = formData;

    const cleanUrl = VIPSDataServiceURL.replace(/\/$/, "");
    const apiUrl = `${cleanUrl}/Ajk/ProcessCustomerMapping`;

    let payload = {};

    if (dialogState.type === 'INSERT') {
        payload = {
            "Id": 0,
            "requestType": REQUEST_TYPES.CREATE,
            "divisionId": divisionId,
            "storeGroup": storeGroup,
            "CommID": commId,
            "BillToId": billToId,
            "BillToNumber": billToId, 
            "WebAccount": webAccNo,
            "ERPAccount": erpAccNo,
            "CommQualifier": "", 
            "addedBy": loggedInUserId, 
            "updatedBy": loggedInUserId,
            "currentDivisionId":""
        };
    } else {
        if (!selectedRow) {
            setSnackbar({ open: true, message: 'Update Failed: Missing Original Data.', severity: 'error' });
            return;
        }

        payload = { ...selectedRow };

        payload["divisionId"] = divisionId;
        
        payload["currentDivisionId"] = selectedRow.divisionId || ""; 
        
        payload["ERPAccount"] = erpAccNo;
        payload["CommQualifier"] = selectedRow.CommQualifier || "";

        const strictId = parseInt(formData.id, 10);
        if (strictId > 0) payload["Id"] = strictId;
        else payload["Id"] = selectedRow.Id;
        
        payload["requestType"] = REQUEST_TYPES.UPDATE;
        payload["updatedBy"] = loggedInUserId; 
    }

    try {
        const response = await axios.post(apiUrl, payload, { headers: requestOptions.headers });
        let result = response.data;
        if (typeof result === 'string') { try { result = JSON.parse(result); } catch (e) { } }

        if (result && (result.success === true || result.Success === true || result.success === "true")) {
             const successMsg = dialogState.type === 'INSERT' ? 'Record Added Successfully' : 'Record Updated Successfully';
             setSnackbar({ open: true, message: successMsg, severity: 'success' });
             setDialogState({ ...dialogState, open: false });
             await fetchData(); 
        } else {
             const msg = result?.message || result?.Message || 'Operation failed on server.';
             setSnackbar({ open: true, message: msg, severity: 'error' });
        }
    } catch (error) {
        let errMsg = 'Operation Failed.';
        if (error.response && error.response.data) {
             let errData = error.response.data;
             if (typeof errData === 'string') { try { const parsed = JSON.parse(errData); errMsg = parsed.message || parsed.Message; } catch(e) { errMsg = errData; } } 
             else { errMsg = errData.Message || errData.message; }
        }
        setSnackbar({ open: true, message: `Error: ${errMsg}`, severity: 'error' });
    }
  };

  const handleRequestDelete = (type, id = null) => setDeleteConfirmation({ open: true, type: type, id: id });
   
  const handleConfirmDelete = async () => {
    const cleanUrl = VIPSDataServiceURL.replace(/\/$/, "");
    const apiUrl = `${cleanUrl}/Ajk/ProcessCustomerMapping`; 

    try {
        let itemsToDelete = [];
        if (deleteConfirmation.type === 'bulk') {
            itemsToDelete = rows.filter(r => selectedIds.includes(r.id));
        } else {
            const row = rows.find(r => r.id === deleteConfirmation.id);
            if (row) itemsToDelete.push(row);
        }

        const deletePromises = itemsToDelete.map(row => {
            const deletePayload = {
                ...row.rawItem,
                "Id": row.originalId,
                "requestType": REQUEST_TYPES.DELETE,
                "updatedBy": loggedInUserId 
            };
            return axios.post(apiUrl, deletePayload, { headers: requestOptions.headers });
        });

        const responses = await Promise.all(deletePromises);
        const allSuccessful = responses.every(res => {
            let data = res.data;
            if (typeof data === 'string') try { data = JSON.parse(data); } catch(e){}
            return data && (data.success === true || data.Success === true);
        });

        if (allSuccessful) {
            setSnackbar({ open: true, message: 'Delete Operation Completed Successfully', severity: 'success' });
            setSelectedIds([]);
            setDeleteConfirmation({ open: false, type: 'single', id: null });
            fetchData();
        } else {
            setSnackbar({ open: true, message: 'Some records could not be deleted.', severity: 'warning' });
            fetchData(); 
        }
    } catch (error) {
        setSnackbar({ open: true, message: 'Delete Failed.', severity: 'error' });
    }
  };

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      const rowDiv = row.divisionId ? row.divisionId.trim().toUpperCase() : '';
      const filterDiv = activeFilters.prodLoc.trim().toUpperCase();
      const matchLoc = activeFilters.prodLoc === 'ALL' || rowDiv === filterDiv;
      const matchStore = activeFilters.storeGroup === 'ALL' || row.storeGroup === activeFilters.storeGroup;
       
      const rowCommIdText = (row.commId || '').trim().toLowerCase();
      const filterCommIdText = activeFilters.commId.trim().toLowerCase();
      const isCommExact = filterCommIdText === '' || rowCommIdText.includes(filterCommIdText);

      const rowBillToIdText = (row.billToId || '').trim().toLowerCase();
      const filterBillToIdText = activeFilters.billToId.trim().toLowerCase();
      const isBillExact = filterBillToIdText === '' || rowBillToIdText.includes(filterBillToIdText);

      const rowErpText = (row.erpAccNo || '').trim().toLowerCase();
      const filterErpText = activeFilters.erpAccNo.trim().toLowerCase();
      const isErpExact = filterErpText === '' || rowErpText.includes(filterErpText);

      return matchLoc && matchStore && isCommExact && isBillExact && isErpExact;
    });
  }, [rows, activeFilters]);

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => { setRowsPerPage(parseInt(event.target.value, 10)); setPage(0); };
  const handleSelectAllClick = (event) => {
    const currentRows = filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    const currentRowIds = currentRows.map(r => r.id);
    if (event.target.checked) setSelectedIds(Array.from(new Set([...selectedIds, ...currentRowIds])));
    else setSelectedIds(selectedIds.filter(id => !currentRowIds.includes(id)));
  };
  const handleSelectOne = (event, id) => {
    const selectedIndex = selectedIds.indexOf(id);
    let newSelected = [];
    if (selectedIndex === -1) newSelected = [...selectedIds, id]; else newSelected = selectedIds.filter(sid => sid !== id);
    setSelectedIds(newSelected);
  };
  const isSelected = (id) => selectedIds.indexOf(id) !== -1;
  const currentRows = filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const isAllCurrentSelected = currentRows.length > 0 && currentRows.every(r => selectedIds.includes(r.id));
  const isSomeCurrentSelected = currentRows.some(r => selectedIds.includes(r.id));
  const isEditMode = dialogState.type === 'EDIT';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: BACKGROUND_BG }}>
       
      <Box component="main" sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', pt: 6, pb: 8 }}>
        
        <Container maxWidth="xl">
             
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ width: 6, height: 32, backgroundColor: PRIMARY_COLOR, borderRadius: 4 }} />
                <Typography variant="h5" sx={{ color: '#1e293b', fontWeight: 700, letterSpacing: '0.5px' }}>AJKS Customer Mapping</Typography>
            </Box>

            <Paper elevation={0} sx={{ p: 3, backgroundColor: 'white', borderRadius: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.03)', mb: 2, border: '1px solid #f0f0f0' }}>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: '700', color: '#64748b', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '1px' }}>Filter Records</Typography>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6} md={2}>
                        <FormControl fullWidth size="small" sx={premiumInputStyle}>
                            <InputLabel>Production Location</InputLabel>
                            <Select value={filters.prodLoc} label="Production Location" onChange={(e) => setFilters({...filters, prodLoc: e.target.value})} MenuProps={menuPropsConfig}>
                                <MenuItem value="ALL">ALL</MenuItem>
                                {divisionOptions.map((option) => (<MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <FormControl fullWidth size="small" sx={premiumInputStyle}>
                            <InputLabel>Store Group</InputLabel>
                            <Select value={filters.storeGroup} label="Store Group" onChange={(e) => setFilters({...filters, storeGroup: e.target.value})} MenuProps={menuPropsConfig}>
                                <MenuItem value="ALL">ALL</MenuItem>
                                {storeOptions.map((option) => (<MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}><TextField fullWidth label="Comm ID" variant="outlined" size="small" sx={premiumInputStyle} value={filters.commId} onChange={(e) => setFilters({...filters, commId: e.target.value})} /></Grid>
                    <Grid item xs={12} sm={6} md={2}><TextField fullWidth label="Bill To ID" variant="outlined" size="small" sx={premiumInputStyle} value={filters.billToId} onChange={(e) => setFilters({...filters, billToId: e.target.value})} /></Grid>
                    <Grid item xs={12} sm={6} md={2}><TextField fullWidth label="ERP Acc No" variant="outlined" size="small" sx={premiumInputStyle} value={filters.erpAccNo} onChange={(e) => setFilters({...filters, erpAccNo: e.target.value})} /></Grid>
                    <Grid item xs={12} sm={6} md={2}><Button variant="contained" sx={{ height: '42px', width: '100%', borderRadius: '10px', background: BUTTON_GRADIENT, fontWeight: '600', textTransform: 'none' }} startIcon={<SearchIcon />} onClick={handleSearch}>Search</Button></Grid>
                </Grid>
            </Paper>

            <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-end', mb: 0, px: 0, gap: 0.5 }}>
                <Button variant="contained" sx={{ height: 40, borderRadius: '12px 12px 0 0', textTransform: 'none', fontWeight: 600, px: 4, background: BUTTON_GRADIENT }} startIcon={<AddIcon />} onClick={handleOpenInsert}>Insert New</Button>
                {selectedIds.length > 0 && (
                    <Fade in={selectedIds.length > 0}>
                        <Button variant="contained" color="error" sx={{ height: 40, borderRadius: '12px 12px 0 0', textTransform: 'none', fontWeight: 600, px: 3, backgroundColor: '#ef5350' }} startIcon={<DeleteSweepIcon />} onClick={() => handleRequestDelete('bulk')}>Delete ({selectedIds.length})</Button>
                    </Fade>
                )}
            </Box>

            <Card sx={{ borderRadius: '0 16px 16px 16px', boxShadow: '0px 10px 30px rgba(0,0,0,0.05)', border: 'none', overflow: 'hidden' }}>
                <TableContainer sx={{ maxHeight: 600 }}>
                <Table stickyHeader size="small">
                    <TableHead>
                    <TableRow>
                        <TableCell padding="checkbox" sx={{ backgroundColor: HEADER_BG, borderBottom: 'none', position: 'sticky', top: 0, zIndex: 1000 }}>
                        <Checkbox color="default" indeterminate={isSomeCurrentSelected && !isAllCurrentSelected} checked={isAllCurrentSelected && currentRows.length > 0} onChange={handleSelectAllClick} sx={{ color: 'rgba(255,255,255,0.7)', '&.Mui-checked': { color: 'white' }, '&.MuiCheckbox-indeterminate': { color: 'white' } }} />
                        </TableCell>
                        <TableCell sx={headerStyle} width={100}>Action</TableCell>
                        <TableCell sx={headerStyle}>Production Location</TableCell> 
                        <TableCell sx={headerStyle}>CommID</TableCell>
                        <TableCell sx={headerStyle}>BillToID</TableCell>
                        <TableCell sx={headerStyle}>Store Group</TableCell>
                        <TableCell sx={headerStyle}>Web Acc No</TableCell>
                        <TableCell sx={headerStyle}>ERP Acc No</TableCell>
                        <TableCell sx={headerStyle}>Date Added</TableCell>
                        <TableCell sx={headerStyle}>Added By</TableCell>
                        <TableCell sx={headerStyle}>Date Updated</TableCell>
                        <TableCell sx={headerStyle}>Updated By</TableCell>
                    </TableRow>
                    </TableHead>
                    <TableBody>
                    {loading ? (
                        <TableRow><TableCell colSpan={12} align="center" sx={{ py: 10 }}><CircularProgress sx={{ color: PRIMARY_COLOR }} /><Typography variant="body2" sx={{ mt: 2, color: '#555' }}>Loading records...</Typography></TableCell></TableRow>
                    ) : filteredRows.length === 0 ? (
                        <TableRow><TableCell colSpan={12} align="center" sx={{ py: 6, color: '#757575', fontStyle: 'italic' }}>No customer mappings found.</TableCell></TableRow>
                    ) : filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, index) => {
                            const isItemSelected = isSelected(row.id);
                            return (
                                <TableRow key={row.id} sx={{ '&:hover': { backgroundColor: HOVER_ROW_COLOR }, backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8f9fa' }} selected={isItemSelected}>
                                <TableCell padding="checkbox"><Checkbox color="primary" checked={isItemSelected} onChange={(event) => handleSelectOne(event, row.id)} /></TableCell>
                                <TableCell>
                                    <Tooltip title="Edit"><IconButton size="small" sx={{ color: PRIMARY_COLOR }} onClick={() => handleOpenEdit(row)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                                    <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => handleRequestDelete('single', row.id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                                </TableCell>
                                <TableCell><Chip label={row.divisionId} size="small" sx={{ height: 24, fontSize: '0.75rem', fontWeight: 700, backgroundColor: '#e0f2f1', color: '#00695c', borderRadius: 1.5 }} /></TableCell>
                                <TableCell>{row.commId}</TableCell>
                                <TableCell>{row.billToId}</TableCell>
                                <TableCell>{row.storeGroup}</TableCell>
                                <TableCell>{row.webAccNo}</TableCell>
                                <TableCell>{row.erpAccNo}</TableCell>
                                <TableCell>{row.dateAdded}</TableCell>
                                <TableCell sx={{ color: '#64748b', fontSize: '0.85rem' }}>{row.addedBy}</TableCell>
                                <TableCell>{row.dateUpdated}</TableCell>
                                <TableCell sx={{ color: '#64748b', fontSize: '0.85rem' }}>{row.updatedBy}</TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
                </TableContainer>
                <TablePagination
                rowsPerPageOptions={[10, 20, 50, 100]} component="div" count={filteredRows.length} rowsPerPage={rowsPerPage} page={page} onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{ backgroundColor: '#fff', borderTop: '1px solid #f0f0f0', '.MuiTablePagination-toolbar': { minHeight: '52px', paddingRight: '16px' }, '.MuiTablePagination-selectLabel': { margin: 0, fontSize: '0.875rem', fontWeight: 500, color: '#64748b' }, '.MuiTablePagination-select': { paddingTop: '6px', paddingBottom: '6px', fontSize: '0.875rem', fontWeight: 500 }, '.MuiTablePagination-displayedRows': { margin: 0, fontSize: '0.875rem', fontWeight: 500, color: '#64748b' } }}
                />
            </Card>

            <Dialog open={dialogState.open} onClose={() => setDialogState({ ...dialogState, open: false })} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden' } }}>
                <DialogTitle sx={dialogHeaderStyle}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    {dialogState.type === 'INSERT' ? <PostAddIcon fontSize="medium" sx={{ color: '#ffffff !important' }} /> : <EditNoteIcon fontSize="medium" sx={{ color: '#ffffff !important' }} />}
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#ffffff !important', letterSpacing: '0.5px' }}>{dialogState.type === 'INSERT' ? 'Insert New Customer Map' : 'Edit Customer Map'}</Typography>
                </Box>
                <IconButton onClick={() => setDialogState({ ...dialogState, open: false })} sx={{ color: '#ffffff !important' }}><CloseIcon /></IconButton>
                </DialogTitle>
                 
                {/* FORM WRAPPER START */}
                <DialogContent sx={{ backgroundColor: '#fafafa', py: 4 }} dividers>
                <form id="mapping-form" onSubmit={handleFormSubmit}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                            <Autocomplete 
                                freeSolo 
                                disabled={isEditMode} 
                                options={storeOptions.map(o => o.value)} 
                                value={formData.commId} 
                                onChange={handleCommIdChange} 
                                onInputChange={(e, val) => handleCommIdChange(e, val)} 
                                renderInput={(params) => <TextField {...params} required label="CommID" variant="outlined" size="small" sx={premiumInputStyle} />} 
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small" sx={premiumInputStyle} required>
                            <InputLabel>Store Group</InputLabel>
                            <Select value={formData.storeGroup} label="Store Group *" onChange={handleStoreGroupChange} disabled={isEditMode} required MenuProps={menuPropsConfig}>
                            {storeOptions.map((option) => (<MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>))}
                            </Select>
                        </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField required fullWidth label="BillTo ID" variant="outlined" size="small" value={formData.billToId} onChange={(e) => handleInputChange('billToId', e.target.value)} disabled={isEditMode} sx={premiumInputStyle} inputProps={{ maxLength: 10 }} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField required fullWidth label="Web Acc No" variant="outlined" size="small" value={formData.webAccNo} onChange={(e) => handleInputChange('webAccNo', e.target.value)} disabled={isEditMode} sx={premiumInputStyle} inputProps={{ maxLength: 10 }} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small" sx={premiumInputStyle} required>
                            <InputLabel>Production Location</InputLabel>
                            <Select value={formData.divisionId} label="Production Location *" onChange={(e) => handleInputChange('divisionId', e.target.value)} required MenuProps={menuPropsConfig}>
                            {divisionOptions.map((option) => (<MenuItem key={option.value} value={option.value.trim()}>{option.label}</MenuItem>))}
                            </Select>
                        </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField required fullWidth label="ERP Acc No" variant="outlined" size="small" value={formData.erpAccNo} onChange={(e) => handleInputChange('erpAccNo', e.target.value)} sx={premiumInputStyle} inputProps={{ maxLength: 10 }} />
                        </Grid>
                    </Grid>
                </form>
                </DialogContent>
                {/* FORM WRAPPER END */}

                <DialogActions sx={{ backgroundColor: '#f4f2e9', p: 3, borderTop: '1px solid #e0e0e0', justifyContent: 'flex-end' }}>
                <Button onClick={() => setDialogState({ ...dialogState, open: false })} sx={{ color: '#64748b', fontWeight: 600, mr: 1, textTransform: 'none' }}>Cancel</Button>
                {/* LINKED BUTTON TO FORM ID AND TYPE SUBMIT */}
                <Button variant="contained" type="submit" form="mapping-form" startIcon={<SaveIcon />} sx={{ background: BUTTON_GRADIENT, px: 3, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>{dialogState.type === 'INSERT' ? 'Save Record' : 'Update Record'}</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={deleteConfirmation.open} onClose={() => setDeleteConfirmation({ ...deleteConfirmation, open: false })} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden' } }}>
                <DialogTitle sx={dialogHeaderStyle}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WarningIcon sx={{ color: '#ffffff !important' }} />
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#ffffff !important', letterSpacing: '0.5px' }}>Confirm Delete</Typography>
                </Box>
                <IconButton onClick={() => setDeleteConfirmation({ ...deleteConfirmation, open: false })} sx={{ color: '#ffffff !important' }} size="small"><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent sx={{ backgroundColor: '#fafafa', pt: 3, pb: 3 }}>
                <Typography variant="body1" sx={{ mt: 1, color: '#334155', fontWeight: 500 }}>{deleteConfirmation.type === 'bulk' ? `Are you sure you want to delete the ${selectedIds.length} selected records?` : 'Are you sure you want to delete this record?'}</Typography>
                <Typography variant="body2" color="error" sx={{ mt: 2, fontStyle: 'italic', fontWeight: 500 }}>This action cannot be undone.</Typography>
                </DialogContent>
                <DialogActions sx={{ backgroundColor: '#f4f2e9', p: 2, borderTop: '1px solid #e0e0e0' }}>
                <Button onClick={() => setDeleteConfirmation({ ...deleteConfirmation, open: false })} sx={{ color: '#64748b', fontWeight: 600, textTransform: 'none' }}>Cancel</Button>
                <Button variant="contained" color="error" startIcon={<DeleteIcon />} onClick={handleConfirmDelete} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>Delete</Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>{snackbar.message}</Alert>
            </Snackbar>
        </Container>
      </Box>

      <Newfooter />
    </Box>
  );
}
